import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';

const fetchSurveyors = async () => {
    const { data } = await api.get('/company/surveyors');
    return data.data;
};

const createSurveyor = async (payload) => {
    try {
        const { data } = await api.post('/company/surveyors', {
            company_id: payload.company_id,
            full_name: payload.full_name?.trim(),
            email: payload.email?.trim(),
            phone: payload.phone?.trim(),
            password: payload.password || undefined,
            location: payload.location?.trim() || null,
            specialization: payload.specialization?.trim() || null,
            license_number: payload.license_number?.trim() || null,
            experience_years: payload.experience_years ? Number(payload.experience_years) : null,
        });
        return data;
    } catch (error) {
        console.error('Create surveyor error:', error.response?.data);
        if (error.response?.status === 422) {
            const errors = error.response.data.errors || {};
            Object.entries(errors).forEach(([field, messages]) => {
                const msg = Array.isArray(messages) ? messages[0] : messages;
                const label = field
                    .replace('full_name', 'Full Name')
                    .replace('license_number', 'License Number')
                    .replace('experience_years', 'Years of Experience')
                    .replace('company_id', 'Company')
                    .split('_')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
                    .join(' ');
                toast.error(`${label}: ${msg}`);
            });
        } else {
            toast.error(error.response?.data?.message || 'Failed to create surveyor');
        }
        throw error;
    }
};

const updateSurveyor = async ({ id, ...payload }) => {
    try {
        const { data } = await api.put(`/company/surveyors/${id}`, {
            company_id: payload.company_id,
            full_name: payload.full_name?.trim(),
            email: payload.email?.trim(),
            phone: payload.phone?.trim(),
            location: payload.location?.trim() || null,
            specialization: payload.specialization?.trim() || null,
            license_number: payload.license_number?.trim() || null,
            experience_years: payload.experience_years ? Number(payload.experience_years) : null,
        });
        return data;
    } catch (error) {
        console.error('Update surveyor error:', error.response?.data);
        if (error.response?.status === 422) {
            const errors = error.response.data.errors || {};
            Object.entries(errors).forEach(([field, messages]) => {
                const msg = Array.isArray(messages) ? messages[0] : messages;
                toast.error(`${field.replace('_', ' ').toUpperCase()}: ${msg}`);
            });
        } else {
            toast.error(error.response?.data?.message || 'Failed to update surveyor');
        }
        throw error;
    }
};

const deleteSurveyor = async (id) => {
    const { data } = await api.delete(`/company/surveyors/${id}`);
    return data;
};

export const useSurveyors = () => {
    return useQuery({
        queryKey: ['surveyors'],
        queryFn: fetchSurveyors,
        staleTime: 1000 * 60 * 5,
        onError: () => toast.error('Failed to load surveyors'),
    });
};

export const useCreateSurveyor = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createSurveyor,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['surveyors'] });
            toast.success('Surveyor added successfully');
        },
    });
};

export const useUpdateSurveyor = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: updateSurveyor,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['surveyors'] });
            toast.success('Surveyor updated successfully');
        },
    });
};

export const useDeleteSurveyor = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: deleteSurveyor,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['surveyors'] });
            toast.success('Surveyor deleted successfully');
        },
        onError: () => toast.error('Failed to delete surveyor'),
    });
};